import {
	Button,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { DataTable } from "@dune/ui";
import { formatDate, formatPricing, getReceiptStatusLabel } from "@dune/utils";
import { ReceiptStatus, ReceiptStatusValues } from "@dune/vars";
import { ArrowLeftIcon } from "lucide-react";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { match } from "ts-pattern";
import {
	useReceiptQuery,
	useUpdateReceiptStatusMutation,
} from "../../api/queries/receipt.queries";
import PageLayoutWithTabs, {
	TabLayoutErrorSkeleton,
	TabLayoutSkeleton,
} from "../../component/layout/PageLayoutWithTabs";
import { ReceiptStatusBadge } from "../../component/receipt/ReceiptStatusBadge";
import { getReceiptPaymentColumns } from "./data-table/receipt-payment-columns";
import CreatePaymentReceiptModal from "./modals/CreatePaymentReceiptModal";
import UpdateReceiptModal from "./modals/UpdateReceiptModal";
import { useMe } from "../../api/queries/users.queries";
import { RoleEnum } from "../../../../../packages/vars/src";

const ReceiptPage = () => {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const receiptResult = useReceiptQuery(id);
	const { me } = useMe();
	const updateStatusMutation = useUpdateReceiptStatusMutation();

	const renderProductSpecificRows = useMemo(() => {
		if (!receiptResult.isSuccess) return null;

		const receipt = receiptResult.data;

		return match(receipt.productType)
			.with(
				"DO",
				() =>
					receipt.doDetails && (
						<>
							<TableRow>
								<TableCell>Assurance Dommages Ouvrage</TableCell>
								<TableCell>
									{match(receipt.doDetails.doPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.doDetails.doPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.doDetails.doPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Dommages aux Existants</TableCell>
								<TableCell>
									{match(receipt.doDetails.existingDamagesPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.doDetails.existingDamagesPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.doDetails.existingDamagesPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>CNR</TableCell>
								<TableCell>
									{match(receipt.doDetails.cnrPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.doDetails.cnrPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.doDetails.cnrPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Contribution Attentats</TableCell>
								<TableCell>
									{match(receipt.doDetails.terrorismContributionPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.doDetails.terrorismContributionPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.doDetails.terrorismContributionPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Protection Juridique</TableCell>
								<TableCell>
									{match(receipt.pjDetails?.legalProtectionPrimeHT)
										.when(
											(value) =>
												value === null || value === 0 || value === undefined,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.pjDetails?.legalProtectionPrimeTaxes)
										.when(
											(value) =>
												value === null || value === 0 || value === undefined,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.pjDetails?.legalProtectionPrimeTTC)
										.when(
											(value) =>
												value === null || value === 0 || value === undefined,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
						</>
					),
			)
			.with(
				"MRT",
				() =>
					receipt.mrtDetails && (
						<>
							<TableRow>
								<TableCell>Tous Risques Chantier</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.trcPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.trcPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.trcPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Dont Catastrophes Naturelles</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.trcNaturalDisastersPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.trcNaturalDisastersPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.trcNaturalDisastersPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Dont Contribution Attentats</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.trcTerrorismContributionPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.trcTerrorismContributionPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.trcTerrorismContributionPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Responsabilité Civile du Maître d'Ouvrage</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.rcmoPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.rcmoPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.rcmoPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Dommages Ouvrages (Incluant la CNR)</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.doCnrPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.doCnrPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.mrtDetails.doCnrPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
						</>
					),
			)
			.with(
				"TRC",
				() =>
					receipt.trcDetails && (
						<>
							<TableRow>
								<TableCell>Garantie de Dommages</TableCell>
								<TableCell>
									{match(receipt.trcDetails.damageCoveragePrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.damageCoveragePrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.damageCoveragePrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Dont Incendie</TableCell>
								<TableCell>
									{match(receipt.trcDetails.damageCoverageFirePrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.damageCoverageFirePrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.damageCoverageFirePrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Dont Autres Dommages</TableCell>
								<TableCell>
									{match(receipt.trcDetails.damageCoverageOtherDamagesPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(
										receipt.trcDetails.damageCoverageOtherDamagesPrimeTaxes,
									)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.damageCoverageOtherDamagesPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Dont Catastrophes Naturelles</TableCell>
								<TableCell>
									{match(
										receipt.trcDetails.damageCoverageNaturalDisastersPrimeHT,
									)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(
										receipt.trcDetails.damageCoverageNaturalDisastersPrimeTaxes,
									)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(
										receipt.trcDetails.damageCoverageNaturalDisastersPrimeTTC,
									)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>
									Garantie de responsabilité civile du maître de l'ouvrage
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.rcmoPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.rcmoPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.rcmoPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
							<TableRow>
								<TableCell>Contribution Attentats</TableCell>
								<TableCell>
									{match(receipt.trcDetails.terrorismContributionPrimeHT)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.terrorismContributionPrimeTaxes)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
								<TableCell>
									{match(receipt.trcDetails.terrorismContributionPrimeTTC)
										.when(
											(value) => value === null || value === 0,
											() => "-",
										)
										.otherwise((value) => formatPricing(value))}
								</TableCell>
							</TableRow>
						</>
					),
			)
			.with("GFA", () => (
				<TableRow>
					<TableCell colSpan={4} className="text-center text-gray-500">
						Détails GFA non disponibles
					</TableCell>
				</TableRow>
			))
			.with("PJ", () => (
				<TableRow>
					<TableCell colSpan={4} className="text-center text-gray-500">
						Détails PJ non disponibles
					</TableCell>
				</TableRow>
			))
			.exhaustive();
	}, [receiptResult]);

	return match(receiptResult)
		.with({ status: "success" }, ({ data: receipt }) => (
			<PageLayoutWithTabs
				title={
					<div className="flex items-center gap-3">
						{receipt.reference} - {receipt.productType}
					</div>
				}
				subtitle={
					me?.role === RoleEnum.DUNE && receipt.brokerSocietyName
						? `Apporteur: ${receipt.brokerSocietyName}`
						: undefined
				}
				topActions={match(me)
					.with({ role: "DUNE" }, () => (
						<div className="flex flex-row justify-between">
							<Button
								variant="ghost"
								onClick={() => navigate(-1)}
								className="has-[>svg]:px-1"
							>
								<ArrowLeftIcon />
								Retour
							</Button>
							<UpdateReceiptModal receipt={receipt} />
						</div>
					))
					.otherwise(() => (
						<Button
							variant="ghost"
							onClick={() => navigate(-1)}
							className="has-[>svg]:px-1"
						>
							<ArrowLeftIcon />
							Retour
						</Button>
					))}
			>
				<div className="flex flex-row mt-4 mb-8">
					<div className="flex flex-col space-x-2 flex-1">
						<span className="text-xs font-light text-slate-800 uppercase">
							Date d'émission
						</span>
						<span className="text-md font-bold">
							{formatDate(receipt.issueDate)}
						</span>
					</div>
					<div className="flex flex-col space-x-2 flex-1">
						<span className="text-xs font-light text-slate-800 uppercase">
							Prime TTC
						</span>
						<span className="text-md font-bold">
							{match(receipt.totalClientPrimeTTC)
								.when(
									(value) => value === null || value === 0,
									() => "-",
								)
								.otherwise((value) => formatPricing(value))}
						</span>
					</div>
					<div className="flex flex-col space-x-2 flex-1">
						<span className="text-xs font-light text-slate-800 uppercase">
							Prime rémunération déduite
						</span>
						<span className="text-md font-bold">
							{match(receipt.totalClientPrimeWithoutBrokerRemuneration)
								.when(
									(value) => value === null || value === 0,
									() => "-",
								)
								.otherwise((value) => formatPricing(value))}
						</span>
					</div>
					<div className="flex flex-col space-x-2 flex-1">
						<span className="text-xs font-light text-slate-800 uppercase">
							Montant réglé
						</span>
						<span className="text-md font-bold text-green-600">
							{formatPricing(receipt.amountPaid)}
						</span>
					</div>
					<div className="flex flex-col space-x-2 flex-1">
						<span className="text-xs font-light text-slate-800 uppercase">
							Restant dû
						</span>
						<span className="text-md font-bold text-red-700">
							{match(receipt.totalClientPrimeTTC)
								.when(
									(value) => value === null || value === 0,
									() => "-",
								)
								.otherwise((totalGrossPremium) =>
									formatPricing(totalGrossPremium - receipt.amountPaid),
								)}
						</span>
					</div>
					<div className="flex flex-col space-x-2 flex-1 justify-center">
						{me?.role === RoleEnum.DUNE ? (
							<Select
								value={receipt.status}
								onValueChange={(value) => {
									updateStatusMutation.mutate(
										{
											id: receipt.id,
											status: value as ReceiptStatus,
										},
										{
											onSuccess: () => {
												toast.success(
													"Statut de la quittance mis à jour",
												);
											},
											onError: () => {
												toast.error(
													"Erreur lors de la mise à jour du statut",
												);
											},
										},
									);
								}}
							>
								<SelectTrigger className="w-[160px] mt-1">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{Object.values(ReceiptStatusValues).map(
										(statusValue) => (
											<SelectItem
												key={statusValue}
												value={statusValue}
											>
												{getReceiptStatusLabel(statusValue)}
											</SelectItem>
										),
									)}
								</SelectContent>
							</Select>
						) : (
							<span className="mt-1">
								<ReceiptStatusBadge status={receipt.status} />
							</span>
						)}
					</div>
				</div>
				<div className="flex flex-row space-x-6">
					<div className="flex-2/3 space-y-6">
						{match(me)
							.with({ role: "DUNE" }, () => (
								<div className="flex flex-row flex-1 justify-between">
									<h2 className="text-xl font-bold">Règlements</h2>
									<CreatePaymentReceiptModal
										receiptId={receipt.id}
										onSuccess={() => receiptResult.refetch()}
									/>
								</div>
							))
							.otherwise(() => (
								<div className="flex flex-row flex-1">
									<h2 className="text-xl font-bold">Règlements</h2>
								</div>
							))}
						<div className="h-[600px] w-full overflow-auto">
							<DataTable
								data={receipt.payments}
								columns={getReceiptPaymentColumns({
									receiptId: receipt.id,
									me,
								})}
								noResultsText="Aucun règlement trouvé"
							/>
						</div>
					</div>
					<div className="flex-1/3 h-32 space-y-8">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead />
									<TableHead>Prime HT</TableHead>
									<TableHead>Taxes</TableHead>
									<TableHead>Prime TTC</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{renderProductSpecificRows}
								<TableRow>
									<TableCell>Frais de Gestion DUNE</TableCell>
									<TableCell>
										{match(receipt.duneManagementFeesHT)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
									<TableCell>
										{match(receipt.duneManagementFeesTaxes)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
									<TableCell>
										{match(receipt.duneManagementFeesTTC)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell>Frais apporteur</TableCell>
									<TableCell>
										{match(receipt.intermediaryManagementFeesHT)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
									<TableCell>
										{match(receipt.intermediaryManagementFeesTaxes)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
									<TableCell>
										{match(receipt.intermediaryManagementFeesTTC)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell className="font-bold">Total</TableCell>
									<TableCell className="font-bold text-right max-w-[120px] overflow-x-auto">
										{match(receipt.totalClientPrimeHT)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
									<TableCell className="font-bold text-right max-w-[120px] overflow-x-auto">
										{match(receipt.totalClientPrimeTaxes)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value ?? 0))}
									</TableCell>
									<TableCell className="font-bold text-right max-w-[120px] overflow-x-auto">
										{match(receipt.totalClientPrimeTTC)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
						<Table className="col-span-2">
							<TableBody>
								<TableRow>
									<TableCell>Commissions Apporteur</TableCell>
									<TableCell>
										{match(receipt.brokerCommission)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
								</TableRow>
								<TableRow>
									<TableCell className="font-bold">
										Rémunération Apporteur
									</TableCell>
									<TableCell className="font-bold">
										{match(receipt.brokerRemuneration)
											.when(
												(value) => value === null || value === 0,
												() => "-",
											)
											.otherwise((value) => formatPricing(value))}
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</div>
				</div>
			</PageLayoutWithTabs>
		))
		.with({ status: "pending" }, () => <TabLayoutSkeleton tabCount={0} />)
		.with({ status: "error" }, () => (
			<TabLayoutErrorSkeleton
				message="Erreur lors du chargement des informations de la quittance."
				tabCount={0}
			/>
		))
		.exhaustive();
};

export default ReceiptPage;
